import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ProntoLogo from "@/components/ProntoLogo";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import {
  ArrowRight, ArrowLeft, UploadSimple, Sparkle, Spinner, DownloadSimple, ArrowClockwise,
  Image as ImageIcon, House, FileText, Check, Copy, Microphone, Stop,
  Camera,
  WhatsappLogo,
} from "@phosphor-icons/react";

const API = `${BACKEND_URL}/api`;

/**
 * Download diretto del file alla demo: l'utente sta provando Pronto e vuole
 * SALVARE l'output sul disco. Su Windows/Edge `navigator.share` apre il
 * dialog "Condividi" di Microsoft → l'utente non sa come scaricare. Quindi
 * qui forziamo download diretto via blob (semplice, universale).
 */
async function downloadDemoFile(url, filename) {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`http_${res.status}`);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    toast.success("Scaricato. Trovi il file nei Download.");
  } catch (err) {
    console.error("download failed:", err);
    toast.error("Download non riuscito. Riprova.");
  }
}

const STEPS = [
  { id: "sopralluogo", label: "Sopralluogo Live", Icon: Microphone, sub: "Detta · annuncio in 60s" },
  { id: "foto", label: "Foto", Icon: ImageIcon, sub: "Migliora · Arreda · Rinnova" },
  { id: "pianta", label: "Pianta", Icon: House, sub: "Arreda la planimetria" },
];

// Foto d'esempio per chi atterra sulla demo senza una sua foto sottomano —
// principale causa di bounce. Path pubblici già esistenti (usati anche dalla
// landing). Il click le scarica come Blob e le inietta come File regolare nel
// flusso di upload.
const SAMPLE_PHOTOS = [
  { src: "/landing/camera-before.jpg", label: "Camera scura" },
  { src: "/landing/cucina-before.jpg", label: "Cucina disordinata" },
  { src: "/landing/bagno-before.jpg",  label: "Bagno datato" },
];

const SAMPLE_PLAN = { src: "/landing/pianta-before.jpg", label: "Pianta catastale d'esempio" };

async function fetchAsFile(url, filename) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch_${res.status}`);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

const FURNISH_STYLES = [
  { v: "moderno", l: "Moderno" },
  { v: "classico", l: "Classico" },
  { v: "minimalista", l: "Minimalista" },
  { v: "scandinavo", l: "Scandinavo" },
];

export default function Demo() {
  const nav = useNavigate();
  // iter v31 r26 — Deep-link: ?demo=sopralluogo|foto|pianta apre il
  // demo specifico al primo render. Consente alla landing di linkare
  // le 3 card "prova subito" direttamente al demo giusto.
  const [step, setStep] = useState(() => {
    try {
      const v = new URL(window.location.href).searchParams.get("demo");
      if (v && ["sopralluogo", "foto", "pianta"].includes(v)) return v;
    } catch { /* noop */ }
    return "sopralluogo";
  });
  const [quota, setQuota] = useState(null);
  const [emailKey, setEmailKey] = useState(() => {
    // Read ?email= from URL once and persist it
    try {
      const url = new URL(window.location.href);
      const e = url.searchParams.get("email");
      if (e) {
        localStorage.setItem("vista_demo_email", e);
        return e;
      }
    } catch { /* noop */ }
    return localStorage.getItem("vista_demo_email") || "";
  });

  const refreshQuota = async () => {
    const params = emailKey ? { email: emailKey } : {};
    try {
      const { data } = await axios.get(`${API}/public/demo/quota`, { params });
      setQuota(data);
    } catch { /* noop */ }
  };

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { refreshQuota(); }, [emailKey]);

  const isWhitelisted = quota?.whitelisted === true;

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col">
      {/* Header */}
      <header className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4 flex items-center justify-between border-b border-[#E5E7E0] bg-white gap-3">
        <Link to="/" className="flex items-center shrink-0" data-testid="brand-link-demo">
          <img
            src="/logo/full-with-tagline.png?v=2"
            alt="Pronto · per agenti immobiliari"
            className="h-12 sm:h-14 w-auto select-none"
            draggable="false"
          />
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3 text-sm">
          <Link to="/" className="text-[#6B6A65] hover:text-[#7C8B6E] flex items-center gap-1.5 px-2 py-1.5" aria-label="Indietro" data-testid="back-to-landing">
            <ArrowLeft size={14} /> <span className="hidden sm:inline">Indietro</span>
          </Link>
          <Link to="/register" className="btn-primary text-xs sm:text-sm px-3 sm:px-4 py-2 whitespace-nowrap" data-testid="demo-register-link">
            Registrati gratuitamente
          </Link>
        </nav>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 lg:px-10 py-8 lg:py-12">
        {/* Title — iter v31 r46 — User: «troppo testo, mi perdo. Tono più
            "guarda cosa fa"». Snellito da 3 frasi a 1. */}
        <div className="max-w-2xl">
          <h1 className="font-editorial text-3xl lg:text-5xl leading-[1.05] tracking-tight" data-testid="demo-title">
            Guarda cosa fa Pronto.{" "}
            <span className="italic-accent text-[#7C8B6E]">
              {isWhitelisted ? "Prove illimitate" : "In 60 secondi."}
            </span>
          </h1>
          <p className="mt-3 text-[14px] text-[#6B6A65]">
            Senza registrazione · 3 prove gratis
          </p>
        </div>

        {/* iter v31 r27 — rimosso il box "Cosa puoi provare adesso" (demo-howto-card):
            ripeteva esattamente lo stepper sotto e affollava la pagina. User: "si
            ripetono, eliminare uno dei due, semplificare". */}

        {/* Stepper — iter v32 r17: User: «allinea i testi dentro o centrali,
            manca l'icona del sopralluogo, fai che siano tutti uguali e
            ordinati». Layout uniformato: icona grande sopra al centro,
            label sotto, sub-line in fondo. min-height fissa per evitare
            che le card saltino di altezza. */}
        <div className="mt-8 grid grid-cols-3 gap-2" data-testid="demo-stepper">
          {STEPS.map(({ id, label, Icon, sub }) => {
            const active = step === id;
            return (
              <button
                key={id}
                onClick={() => setStep(id)}
                className={`text-center px-2 py-3 sm:px-4 sm:py-4 rounded-md border transition-all min-h-[112px] sm:min-h-[124px] flex flex-col items-center justify-center gap-1.5 ${
                  active
                    ? "bg-[#1C1C1A] border-[#1C1C1A] text-white"
                    : "bg-white border-[#E5E7E0] text-[#1C1C1A] hover:border-[#7C8B6E]/40"
                }`}
                data-testid={`step-${id}`}
              >
                <Icon
                  size={22}
                  weight={active ? "fill" : "regular"}
                  className={active ? "text-[#A8B89A]" : "text-[#7C8B6E]"}
                />
                <span className={`text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] font-semibold ${active ? "text-white/70" : "text-[#1C1C1A]/60"}`}>
                  {label}
                </span>
                <div className={`text-[11.5px] sm:text-[12.5px] leading-snug font-medium ${active ? "text-white" : "text-[#1C1C1A]"}`}>
                  {sub}
                </div>
              </button>
            );
          })}
        </div>

        {/* Step content */}
        <div className="mt-8">
          {step === "sopralluogo" && (
            <DemoSopralluogoLive
              emailKey={emailKey}
              quota={quota}
              isWhitelisted={isWhitelisted}
              onUsed={refreshQuota}
            />
          )}
          {step === "foto" && (
            <DemoEnhance
              emailKey={emailKey}
              quota={quota}
              isWhitelisted={isWhitelisted}
              onUsed={refreshQuota}
            />
          )}
          {step === "pianta" && (
            <DemoFurnishPlan
              emailKey={emailKey}
              quota={quota}
              isWhitelisted={isWhitelisted}
              onUsed={refreshQuota}
            />
          )}
        </div>

        {/* Final CTA */}
        <div className="mt-14 bg-[#7C8B6E] text-white rounded-md p-8 lg:p-10 flex items-center justify-between gap-6 flex-wrap" data-testid="demo-final-cta">
          <div className="max-w-xl">
            <h3 className="font-editorial text-3xl lg:text-4xl leading-tight">
              Hai visto cosa fa.<br/>
              <span className="italic text-[#F1F4ED]">Ora usalo sui tuoi annunci.</span>
            </h3>
            <p className="mt-3 text-sm opacity-85">
              Gratuita: foto, pianta arredata, descrizioni e vetrina immobile.
              Tutto incluso, senza carta. La filigrana resta nel periodo gratuito, sparisce con il piano Pro.
            </p>
          </div>
          <button
            onClick={() => nav("/register")}
            className="bg-[#1C1C1A] hover:bg-black text-white px-7 py-3.5 rounded-sm font-medium inline-flex items-center gap-2 transition-colors whitespace-nowrap"
            data-testid="demo-register-final"
          >
            Crea il mio account <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </main>

      <footer className="border-t border-[#E5E7E0] py-6 px-6 lg:px-10 text-xs text-[#6B6A65] flex flex-col sm:flex-row justify-between gap-2 bg-white">
        <span>© {new Date().getFullYear()} Pronto · Pensato in Italia</span>
        <span>Le foto caricate in demo non vengono conservate.</span>
      </footer>
    </div>
  );
}

// iter v31 r62 — Le 3 azioni che l'utente può provare senza registrarsi.
// Quota condivisa: 3 prove totali tra Migliora + Arreda + Restyling.
const PHOTO_ACTIONS = [
  {
    id: "enhance",
    label: "Migliora",
    hint: "Luce e colore",
    sub: "Sistemiamo luce, colori, geometrie",
    endpoint: "/public/demo/enhance",
    needsStyle: false,
  },
  {
    id: "stage",
    label: "Arreda",
    hint: "Stanza vuota o cantiere",
    sub: "Stanza vuota, in costruzione o spoglia → arredata in stile",
    endpoint: "/public/demo/stage",
    needsStyle: true,
  },
  {
    id: "restyle",
    label: "Rinnova",
    hint: "Mobili vecchi",
    sub: "Arredo datato → nuovo, senza toccare le strutture",
    endpoint: "/public/demo/restyle",
    needsStyle: true,
  },
];

const DEMO_STYLES = [
  { v: "moderno", l: "Moderno" },
  { v: "classico", l: "Classico" },
  { v: "minimalista", l: "Minimal" },
  { v: "scandinavo", l: "Scandinavo" },
];

// ── Step 1: Migliora / Arreda / Rinnova foto ─────────────────────────────────
function DemoEnhance({ emailKey, quota, isWhitelisted, onUsed }) {
  const [action, setAction] = useState("enhance"); // enhance | stage | restyle
  const [style, setStyle] = useState("moderno");
  const [originalFile, setOriginalFile] = useState(null);
  const [originalPreview, setOriginalPreview] = useState(null);
  const [enhancedUrl, setEnhancedUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const remaining = quota?.remaining ?? 3;
  const limit = quota?.limit ?? 3;
  const outOfQuota = !isWhitelisted && remaining === 0;
  const actionMeta = PHOTO_ACTIONS.find((a) => a.id === action) || PHOTO_ACTIONS[0];

  const onPickFile = () => fileInputRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Foto troppo grande (max 10 MB)"); return; }
    setOriginalFile(f);
    setEnhancedUrl(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setOriginalPreview(ev.target.result);
      // iter v31 r46 — Auto-process: l'utente carica e Pronto modifica da solo.
      // User: «fagli vedere quello che si prepara, modificagli una in automatico».
      setTimeout(() => onProcess(f), 200);
    };
    reader.readAsDataURL(f);
  };
  // iter 261: chip "prova con un esempio nostro" → principale fix conversion
  // per chi atterra qui senza una foto sottomano.
  const onPickSample = async (src, filename) => {
    try {
      const f = await fetchAsFile(src, filename);
      setOriginalFile(f);
      setEnhancedUrl(null);
      setOriginalPreview(src);
      // Auto-process anche sui sample (stesso pattern del file upload)
      setTimeout(() => onProcess(f), 200);
    } catch {
      toast.error("Esempio non caricato, riprova.");
    }
  };
  const onProcess = async (fileArg = null) => {
    const file = fileArg || originalFile;
    if (!file) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = emailKey ? { email: emailKey } : {};
      const { data } = await axios.post(`${API}/public/demo/enhance`, fd, {
        params, headers: { "Content-Type": "multipart/form-data" }, timeout: 90_000,
      });
      setEnhancedUrl(`data:${data.mime};base64,${data.enhanced_b64}`);
      onUsed();
      toast.success("Ecco come sarebbe la tua foto.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova.");
    } finally { setProcessing(false); }
  };
  const onReset = () => {
    setOriginalFile(null); setOriginalPreview(null); setEnhancedUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // iter v31 r62 — Titolo dinamico in base all'azione scelta.
  const titleByAction = {
    enhance: "Carica una foto, te la miglioriamo",
    stage: "Carica una stanza vuota (o un cantiere), te la arrediamo",
    restyle: "Carica una stanza arredata, le rinnoviamo i mobili",
  };
  // Quando l'utente cambia azione DOPO aver caricato una foto, reset il
  // risultato per evitare confusione (la foto vecchia rimane, ma il risultato
  // mostrato non è più coerente con l'azione attuale).
  const onChangeAction = (newAction) => {
    if (processing) return;
    setAction(newAction);
    setEnhancedUrl(null);
  };

  return (
    <div data-testid="demo-step-foto">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A65]">Foto</span>
          <h2 className="font-editorial text-2xl mt-1" data-testid="demo-foto-title">
            {titleByAction[action]}
          </h2>
        </div>
        <span className="text-xs text-[#6B6A65]">
          {isWhitelisted ? "Prove illimitate" : `Prove rimaste: ${remaining}/${limit}`}
        </span>
      </div>

      {/* iter v31 r62 — Toolbar 3 azioni provabili senza registrazione.
          Quota condivisa: l'utente sceglie come spendere le 3 prove. */}
      <div
        className="flex gap-1.5 sm:gap-2 mb-3 flex-wrap"
        role="tablist"
        aria-label="Scegli cosa provare"
        data-testid="demo-foto-action-toolbar"
      >
        {PHOTO_ACTIONS.map((a) => {
          const isActive = a.id === action;
          return (
            <button
              key={a.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChangeAction(a.id)}
              disabled={processing}
              className={`flex-1 min-w-[110px] px-3 py-2.5 rounded-md border text-left transition-all ${
                isActive
                  ? "bg-[#1C1C1A] text-white border-[#1C1C1A] shadow-[0_2px_8px_rgba(28,28,26,0.18)]"
                  : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E] disabled:opacity-50"
              }`}
              data-testid={`demo-foto-action-${a.id}`}
            >
              <div className="font-editorial text-[15px] leading-tight">{a.label}</div>
              <div className={`text-[10.5px] mt-0.5 ${isActive ? "text-white/70" : "text-[#9A968B]"}`}>
                {a.hint}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selettore stile · visibile solo per Arreda/Rinnova */}
      {actionMeta.needsStyle && (
        <div
          className="flex flex-wrap gap-1.5 mb-4 items-center"
          data-testid="demo-foto-style-picker"
        >
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold mr-1">
            Stile
          </span>
          {DEMO_STYLES.map((s) => (
            <button
              key={s.v}
              type="button"
              onClick={() => setStyle(s.v)}
              disabled={processing}
              className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${
                s.v === style
                  ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
                  : "bg-white text-[#3A3A36] border-[#E5E7E0] hover:border-[#7C8B6E] disabled:opacity-50"
              }`}
              data-testid={`demo-style-${s.v}`}
            >
              {s.l}
            </button>
          ))}
        </div>
      )}

      {/* iter v31 r46 — Tolto il box "3 strumenti foto": troppo testo,
          rallentava il "vedi cosa fa". I 3 strumenti vivono nella dashboard. */}

      {!originalPreview ? (
        <>
          <button
            onClick={outOfQuota ? undefined : onPickFile}
            disabled={outOfQuota}
            className={`w-full bg-white border-2 border-dashed rounded-md p-12 text-center transition-colors ${
              outOfQuota ? "border-[#E5E7E0] opacity-60 cursor-not-allowed" : "border-[#E5E7E0] hover:border-[#7C8B6E] cursor-pointer"
            }`}
            data-testid="demo-upload-zone"
          >
            <UploadSimple size={36} weight="light" className="mx-auto text-[#7C8B6E]" />
            <h3 className="font-editorial text-2xl mt-3">
              {outOfQuota ? "Prove gratuite esaurite" : "Trascina o scegli una foto"}
            </h3>
            <p className="mt-1.5 text-sm text-[#6B6A65]">
              {outOfQuota ? "Registrati gratuitamente per continuare." : "JPG, PNG o WebP · max 10 MB · meglio se in formato orizzontale"}
            </p>
            {!outOfQuota && (
              <span className="btn-primary mt-5 inline-flex">
                <UploadSimple size={15} weight="bold" /> Seleziona foto
              </span>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid="demo-file-input" />
          </button>

          {/* Sample chips — fallback per chi non ha foto sottomano */}
          {!outOfQuota && (
            <div className="mt-4" data-testid="demo-samples">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold mb-2.5">
                Oppure prova con un esempio nostro
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {SAMPLE_PHOTOS.map((s, i) => (
                  <button
                    key={s.src}
                    type="button"
                    onClick={() => onPickSample(s.src, `pronto-esempio-${i + 1}.jpg`)}
                    className="group relative aspect-[4/3] rounded-md overflow-hidden border border-[#E5E7E0] hover:border-[#7C8B6E] transition-all bg-[#F1F4ED]"
                    data-testid={`demo-sample-${i}`}
                  >
                    <img src={s.src} alt={s.label} className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.03]" />
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent text-white text-[10.5px] font-medium px-2 py-1.5 text-left">
                      {s.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div>
          {enhancedUrl ? (
            <>
              <BeforeAfterSlider beforeSrc={originalPreview} afterSrc={enhancedUrl} height="h-[55vh]" />
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => downloadDemoFile(enhancedUrl, "pronto-demo-foto.jpg")}
                  className="btn-outline"
                  data-testid="demo-download-btn"
                >
                  <DownloadSimple size={16} /> Scarica con filigrana
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    "Guarda cosa fa Pronto alle foto delle case. In 10 secondi, senza account. Provala anche tu: https://prontoimmo.it/prova?demo=foto"
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
                  data-testid="demo-foto-share-whatsapp"
                >
                  <WhatsappLogo size={16} weight="fill" /> Condividi su WhatsApp
                </a>
                <button onClick={onReset} className="btn-outline text-sm" data-testid="demo-reset-btn">
                  <ArrowClockwise size={16} /> Prova un&apos;altra
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="relative aspect-[16/10] rounded-md overflow-hidden bg-[#F1F4ED]" data-testid="demo-original-preview">
                <img src={originalPreview} alt="" className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.16em] bg-white/90 px-2 py-1 rounded">Originale</span>
                {processing && (
                  <div className="absolute inset-0 bg-[#1C1C1A]/65 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Spinner size={36} className="animate-spin mx-auto" weight="bold" />
                      <p className="mt-3 font-editorial text-xl">Sto lavorando...</p>
                      <p className="mt-0.5 text-xs opacity-75">15-30 secondi</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {/* iter v31 r46 — Tolto "Migliora foto": l'auto-process
                    parte da solo dopo l'upload. */}
                <button onClick={onReset} disabled={processing} className="btn-outline text-sm disabled:opacity-50" data-testid="demo-change-photo-btn">
                  Cambia foto
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Step 2: Furnish floor plan ───────────────────────────────────────────────
function DemoFurnishPlan({ emailKey, quota, isWhitelisted, onUsed }) {
  const [planFile, setPlanFile] = useState(null);
  const [planPreview, setPlanPreview] = useState(null);
  const [planVertical, setPlanVertical] = useState(false); // true if image height > width
  const [furnishedUrl, setFurnishedUrl] = useState(null);
  const [style, setStyle] = useState("moderno");
  const [processing, setProcessing] = useState(false);
  const inputRef = useRef(null);
  const remaining = quota?.plan_remaining ?? 1;
  const limit = quota?.plan_limit ?? 1;
  const outOfQuota = !isWhitelisted && remaining === 0;

  const pick = () => inputRef.current?.click();
  const onChange = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10 MB)"); return; }
    setPlanFile(f); setFurnishedUrl(null);
    const r = new FileReader();
    r.onload = (ev) => {
      const dataUrl = ev.target.result;
      setPlanPreview(dataUrl);
      // Auto-detect orientation: rotate only if the source image is taller than wide
      const img = new Image();
      img.onload = () => setPlanVertical(img.naturalHeight > img.naturalWidth);
      img.src = dataUrl;
      // iter v31 r46 — Auto-process: arreda da solo con stile "moderno".
      // L'utente può scegliere altri stili nell'app dopo la registrazione.
      setTimeout(() => onProcess(f), 200);
    };
    r.readAsDataURL(f);
  };
  // iter 261: sample plan loader — fix conversione per chi atterra senza pianta
  const onPickSamplePlan = async () => {
    try {
      const f = await fetchAsFile(SAMPLE_PLAN.src, "pronto-esempio-pianta.jpg");
      setPlanFile(f);
      setFurnishedUrl(null);
      setPlanPreview(SAMPLE_PLAN.src);
      const img = new Image();
      img.onload = () => setPlanVertical(img.naturalHeight > img.naturalWidth);
      img.src = SAMPLE_PLAN.src;
      // Auto-process anche sui sample
      setTimeout(() => onProcess(f), 200);
    } catch {
      toast.error("Esempio non caricato, riprova.");
    }
  };
  const onProcess = async (fileArg = null) => {
    const file = fileArg || planFile;
    if (!file) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = { style, ...(emailKey ? { email: emailKey } : {}) };
      const { data } = await axios.post(`${API}/public/demo/furnish-plan`, fd, {
        params, headers: { "Content-Type": "multipart/form-data" }, timeout: 90_000,
      });
      setFurnishedUrl(`data:${data.mime};base64,${data.furnished_b64}`);
      onUsed();
      toast.success("Pianta arredata!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova.");
    } finally { setProcessing(false); }
  };
  const onReset = () => {
    setPlanFile(null); setPlanPreview(null); setFurnishedUrl(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid="demo-step-pianta">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A65]">Step 2</span>
          <h2 className="font-editorial text-2xl mt-1">Arreda una pianta catastale</h2>
        </div>
        <span className="text-xs text-[#6B6A65]">
          {isWhitelisted ? "Prove illimitate" : `${remaining}/${limit} prove rimaste`}
        </span>
      </div>

      {!planPreview ? (
        <>
          <button
            onClick={outOfQuota ? undefined : pick}
            disabled={outOfQuota}
            className={`w-full bg-white border-2 border-dashed rounded-md p-12 text-center transition-colors ${
              outOfQuota ? "border-[#E5E7E0] opacity-60 cursor-not-allowed" : "border-[#E5E7E0] hover:border-[#7C8B6E] cursor-pointer"
            }`}
            data-testid="demo-plan-zone"
          >
            <House size={36} weight="light" className="mx-auto text-[#7C8B6E]" />
            <h3 className="font-editorial text-2xl mt-3">
              {outOfQuota ? "Prove gratuite esaurite" : "Carica la pianta catastale"}
            </h3>
            <p className="mt-1.5 text-sm text-[#6B6A65]">
              {outOfQuota ? "Registrati gratuitamente per continuare." : "JPG o PNG · pianta in B/N o scala di grigi · max 10 MB"}
            </p>
            {!outOfQuota && (
              <>
                <p className="mt-2 text-[11.5px] text-[#9A968B] max-w-md mx-auto leading-relaxed">
                  Meglio una pianta <strong>nitida</strong>, con le scritte (cucina, bagno, camera) leggibili. Foto sfocate = arredo meno preciso.
                </p>
                <span className="btn-primary mt-5 inline-flex">
                  <UploadSimple size={15} weight="bold" /> Seleziona pianta
                </span>
              </>
            )}
            <input ref={inputRef} type="file" accept="image/*" onChange={onChange} className="hidden" data-testid="demo-plan-input" />
          </button>

          {!outOfQuota && (
            <div className="mt-4" data-testid="demo-plan-sample">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold mb-2.5">
                Oppure prova con una pianta nostra
              </div>
              <button
                type="button"
                onClick={onPickSamplePlan}
                className="group flex items-center gap-3 p-2.5 rounded-md border border-[#E5E7E0] hover:border-[#7C8B6E] transition-all bg-white text-left w-full max-w-md"
                data-testid="demo-plan-sample-btn"
              >
                <div className="w-20 h-16 shrink-0 rounded overflow-hidden bg-[#F1F4ED] border border-[#EAE7DF]">
                  <img src={SAMPLE_PLAN.src} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-[#1C1C1A]">{SAMPLE_PLAN.label}</div>
                  <div className="text-[11px] text-[#9A968B] mt-0.5">Tocca per caricarla nella demo →</div>
                </div>
              </button>
            </div>
          )}
        </>
      ) : (
        <div>
          {furnishedUrl ? (
            <>
              <BeforeAfterSlider beforeSrc={planPreview} afterSrc={furnishedUrl} height="h-[55vh]" rotate={planVertical ? -90 : 0} fit="contain" />
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => downloadDemoFile(furnishedUrl, "pronto-demo-pianta.jpg")}
                  className="btn-outline"
                >
                  <DownloadSimple size={16} /> Scarica con filigrana
                </button>
                <button onClick={onReset} className="btn-outline text-sm">
                  <ArrowClockwise size={16} /> Prova un&apos;altra
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="relative aspect-[4/3] bg-white border border-[#E5E7E0] rounded-md overflow-hidden flex items-center justify-center">
                <img src={planPreview} alt="" className="max-w-full max-h-full object-contain" style={{ transform: "rotate(-90deg)" }} />
                {processing && (
                  <div className="absolute inset-0 bg-[#1C1C1A]/65 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Spinner size={36} className="animate-spin mx-auto" weight="bold" />
                      <p className="mt-3 font-editorial text-xl">Sto arredando...</p>
                      <p className="mt-0.5 text-xs opacity-75">30-60 secondi</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {/* iter v31 r46 — Tolto "Arreda": parte da solo dopo l'upload. */}
                <button onClick={onReset} disabled={processing} className="btn-outline text-sm">
                  Cambia pianta
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ── Step "Sopralluogo Live": detta vocale → annuncio ─────────────────────────
// L'utente registra un vocale di 30-90s come farebbe su WhatsApp a un collega
// e Pronto lo trascrive + trasforma in annuncio pronto. Niente foto in demo:
// le foto si aggiungono dopo, dentro l'app.
function DemoSopralluogoLive({ emailKey, quota, isWhitelisted, onUsed }) {
  // Vocale
  const [recState, setRecState] = useState("idle"); // idle | recording | recorded | processing | done
  const [seconds, setSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [generated, setGenerated] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const blobRef = useRef(null);

  const remaining = quota?.voice_remaining ?? 1;
  const limit = quota?.voice_limit ?? 1;
  const voiceUsedUp = !isWhitelisted && remaining === 0 && recState !== "done";

  // ── Voice handling ──
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mime = candidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        blobRef.current = blob;
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      recRef.current = mr;
      setRecState("recording");
      setSeconds(0);
      tickRef.current = setInterval(() => setSeconds((s) => {
        const next = s + 1;
        if (next >= 90) { stopRecording(); }
        return next;
      }), 1000);
    } catch {
      toast.error("Impossibile accedere al microfono. Concedi i permessi al browser.");
    }
  };
  const stopRecording = () => {
    try { recRef.current?.stop(); } catch { /* noop */ }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecState("recorded");
  };
  const onGenerate = async () => {
    if (!blobRef.current) {
      toast.error("Registra prima un vocale");
      return;
    }
    setRecState("processing");
    try {
      const fd = new FormData();
      fd.append("file", blobRef.current, "sopralluogo.webm");
      const params = emailKey ? { email: emailKey } : {};
      const { data } = await axios.post(`${API}/public/demo/voice`, fd, {
        params, headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000,
      });
      setTranscript(data.transcript || "");
      setGenerated({ data: data.data || {}, description: data.description || "" });
      setRecState("done");
      onUsed();
      toast.success("Annuncio pronto!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova.");
      setRecState("recorded");
    }
  };
  const onResetAll = () => {
    setRecState("idle");
    setAudioUrl(null);
    setTranscript("");
    setGenerated(null);
    blobRef.current = null;
  };
  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiato"));
  };

  const fmt = (n) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;

  return (
    <div data-testid="demo-step-sopralluogo">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A65]">Sopralluogo Live</span>
          <h2 className="font-editorial text-2xl mt-1">Detta. Annuncio pronto.</h2>
          <p className="text-sm text-[#6B6A65] mt-1.5 max-w-xl leading-relaxed">
            Registra un vocale di 30–60s come faresti su WhatsApp a un collega.
            Pronto lo trascrive e ti restituisce l&apos;annuncio già scritto.
          </p>
        </div>
        <span className="text-xs text-[#6B6A65]">
          {isWhitelisted ? "Prove illimitate" : `Prove rimaste oggi: ${remaining}/${limit}`}
        </span>
      </div>

      {/* iter v31 r28 — Nota chiara: niente foto in demo */}
      <div className="mb-5 bg-[#F1F4ED] border border-[#E5E7E0] rounded-md p-3.5 flex items-start gap-2.5" data-testid="sop-photos-later-note">
        <Camera size={16} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
        <p className="text-[12.5px] text-[#1C1C1A] leading-relaxed">
          <strong>Le foto le aggiungi dopo, dentro l&apos;app.</strong>{" "}
          <span className="text-[#6B6A65]">Qui ti facciamo provare la parte più potente: dal vocale all&apos;annuncio in 60 secondi.</span>
        </p>
      </div>

      {recState !== "done" ? (
        <div className="space-y-5">
          {/* ── Guida testuale: cosa dire ── */}
          <section className="bg-white border border-[#E5E7E0] rounded-xl p-5" data-testid="sop-guide-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-[#7C8B6E] text-white text-[11px] font-bold inline-flex items-center justify-center">1</span>
              <h3 className="font-editorial text-lg">Cosa dire nel vocale</h3>
            </div>
            <p className="text-[12.5px] text-[#6B6A65] mb-3 leading-relaxed">
              Parla come faresti a voce con un collega. Pronto capisce anche se
              salti dei pezzi o torni indietro.
            </p>
            <ul className="text-[13px] text-[#1C1C1A] space-y-1.5 leading-relaxed">
              <li className="flex gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 shrink-0" /> Tipo casa, città, zona, piano</li>
              <li className="flex gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 shrink-0" /> Metri quadri, locali, bagni</li>
              <li className="flex gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 shrink-0" /> Stato (ristrutturato, da ristrutturare, abitabile…)</li>
              <li className="flex gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 shrink-0" /> Plus (terrazzo, box, esposizione, ascensore…)</li>
              <li className="flex gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 shrink-0" /> Prezzo richiesto</li>
            </ul>
            <p className="text-[12px] text-[#6B6A65] mt-4 italic leading-relaxed border-l-2 border-[#7C8B6E]/40 pl-3">
              Esempio: «Trilocale a Milano, Porta Romana, 95 mq, terzo piano con
              ascensore, ristrutturato 2023, parquet rovere, doppia esposizione,
              480 mila euro.»
            </p>
          </section>

          {/* ── Registra ── */}
          <section className="bg-white border border-[#E5E7E0] rounded-xl p-5" data-testid="sop-voice-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-[#7C8B6E] text-white text-[11px] font-bold inline-flex items-center justify-center">2</span>
              <h3 className="font-editorial text-lg">Registra il vocale</h3>
            </div>

            {recState === "idle" && (
              <div className="bg-[#FAFAF7] border border-[#E5E7E0] rounded-lg p-6 text-center" data-testid="sop-voice-idle">
                <button
                  onClick={startRecording}
                  disabled={voiceUsedUp}
                  className="w-20 h-20 rounded-full bg-[#7C8B6E] hover:bg-[#6B7B5E] text-white flex items-center justify-center mx-auto transition disabled:bg-[#B5BBAE]"
                  data-testid="sop-voice-record-btn"
                >
                  <Microphone size={32} weight="fill" />
                </button>
                <p className="mt-4 text-sm text-[#1C1C1A] font-medium">
                  {voiceUsedUp ? "Prova vocale già usata" : "Tocca per registrare"}
                </p>
                <p className="mt-1 text-xs text-[#6B6A65]">
                  {voiceUsedUp ? "Registrati per continuare." : "Max 90 secondi · in italiano"}
                </p>
              </div>
            )}

            {recState === "recording" && (
              <div className="bg-[#1C1C1A] text-white rounded-lg p-6 text-center" data-testid="sop-voice-recording">
                <button
                  onClick={stopRecording}
                  className="w-20 h-20 rounded-full bg-[#B8624A] hover:bg-[#9A4F3D] text-white flex items-center justify-center mx-auto transition animate-pulse"
                  data-testid="sop-voice-stop-btn"
                >
                  <Stop size={32} weight="fill" />
                </button>
                <p className="mt-4 text-3xl font-editorial tabular-nums">{fmt(seconds)}</p>
                <p className="mt-2 text-[10.5px] text-white/60 uppercase tracking-[0.16em]">In registrazione · tocca per stop</p>
              </div>
            )}

            {recState === "recorded" && audioUrl && (
              <div className="bg-[#FAFAF7] border border-[#E5E7E0] rounded-lg p-5 space-y-3" data-testid="sop-voice-recorded">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm text-[#1C1C1A] font-medium">Vocale di {fmt(seconds)} pronto.</span>
                  <button
                    onClick={() => { setRecState("idle"); setAudioUrl(null); blobRef.current = null; }}
                    className="text-xs text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1"
                    data-testid="sop-voice-rerec"
                  >
                    <ArrowClockwise size={12} /> Ri-registra
                  </button>
                </div>
                <audio src={audioUrl} controls className="w-full" />
              </div>
            )}

            {recState === "processing" && (
              <div className="bg-[#FAFAF7] border border-[#E5E7E0] rounded-lg p-10 text-center" data-testid="sop-voice-processing">
                <Spinner size={40} weight="bold" className="text-[#7C8B6E] animate-spin mx-auto" />
                <p className="mt-4 text-sm text-[#1C1C1A] font-medium">Trascrizione + scrittura annuncio in corso…</p>
                <p className="mt-1 text-xs text-[#6B6A65]">Di solito 10–15 secondi</p>
              </div>
            )}
          </section>

          {/* ── CTA: Genera annuncio ── */}
          {recState === "recorded" && (
            <button
              onClick={onGenerate}
              disabled={voiceUsedUp}
              className="btn-primary w-full justify-center text-base py-4 disabled:opacity-60"
              data-testid="sop-generate-btn"
            >
              <Sparkle size={18} weight="fill" /> Genera l&apos;annuncio dal vocale
            </button>
          )}
        </div>
      ) : (
        // ── Risultato finale: trascrizione + annuncio ──
        <div className="space-y-5" data-testid="sop-result">
          {/* Header scheda */}
          <div className="bg-[#7C8B6E] text-white rounded-xl p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 inline-flex items-center justify-center shrink-0">
              <House size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Scheda casa generata</div>
              <div className="font-editorial text-xl truncate mt-0.5">
                {generated.data?.title || generated.data?.property_type || "Annuncio pronto"}
              </div>
            </div>
            <button
              onClick={onResetAll}
              className="text-[11px] underline underline-offset-2 hover:opacity-90 inline-flex items-center gap-1 shrink-0"
              data-testid="sop-restart-btn"
            >
              <ArrowClockwise size={12} /> Ricomincia
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            {/* Trascrizione + dati estratti */}
            <div className="bg-white border border-[#E5E7E0] rounded-xl p-5">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-2 flex items-center gap-1.5">
                <Microphone size={12} weight="fill" className="text-[#7C8B6E]" />
                Trascrizione del vocale
              </div>
              <p className="text-[13px] text-[#1C1C1A] italic leading-relaxed" data-testid="sop-transcript">
                «{transcript || "—"}»
              </p>

              {/* Dati estratti */}
              {generated.data && Object.keys(generated.data).filter((k) => generated.data[k] != null && generated.data[k] !== "").length > 0 && (
                <div className="mt-5 pt-4 border-t border-[#E5E7E0]">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-2">
                    Dati estratti
                  </div>
                  <div className="space-y-1 text-[12px]">
                    {Object.entries(generated.data)
                      .filter(([, v]) => v != null && v !== "")
                      .map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3">
                          <span className="text-[#6B6A65] capitalize">{k.replace(/_/g, " ")}</span>
                          <span className="text-[#1C1C1A] font-medium text-right">{String(v)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Nota foto */}
              <div className="mt-5 pt-4 border-t border-[#E5E7E0] flex items-start gap-2">
                <Camera size={14} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
                <p className="text-[11.5px] text-[#6B6A65] leading-relaxed">
                  Le foto le aggiungi dentro l&apos;app: si attaccano in automatico a questa scheda.
                </p>
              </div>
            </div>

            {/* Annuncio generato */}
            <div className="bg-[#FAFAF7] border border-[#E5E7E0] rounded-xl p-5 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.16em] text-[#7C8B6E] font-semibold inline-flex items-center gap-1.5">
                  <FileText size={12} weight="fill" /> Annuncio pronto
                </span>
                <button
                  onClick={() => copyText(generated.description)}
                  className="text-xs text-[#7C8B6E] hover:text-[#5C6B4E] inline-flex items-center gap-1"
                  data-testid="sop-copy-btn"
                >
                  <Copy size={12} /> Copia
                </button>
              </div>
              <p className="text-[14px] text-[#1C1C1A] leading-[1.65] whitespace-pre-line font-serif flex-1" data-testid="sop-description">
                {generated.description}
              </p>
              <div className="mt-5 pt-4 border-t border-[#E5E7E0] flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[11px] text-[#6B6A65] italic">
                  Vuoi anche vetrina immobile, story IG e caption? È incluso con l&apos;account.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

